import { UserProfileCreateDto, UserProfileRole } from '@@types/api.types';
import { TITLE_MAX_LENGTH, TITLE_VALIDATION_MESSAGES } from '@azymmetric/shared/validation/validation.constants';
import { TITLE_MIN_LENGTH } from '@azymmetric/shared/validation/validation.constants';
import { zCoercedOptional } from '@utils/zod.util';
import { z } from 'zod';

/**
 * Schema for validating user profile creation data.
 * Validates all required fields for creating a new user profile including
 * personal information, contact details, and role assignment.
 */
export const userProfileCreateSchema = z
  .object({
    categoryId: zCoercedOptional(z.string().uuid().optional()),
    country: zCoercedOptional(
      z
        .string()
        .regex(/^[A-Z]{2}$/, { message: 'Please select a valid citizenship country.' })
        .optional(),
    ),
    countryCode: zCoercedOptional(z.string().optional()),
    firstName: z.string().min(1, { message: 'First name is required' }),
    hasAcceptedTerms: z.boolean(),
    jobRoleId: zCoercedOptional(z.string().uuid().optional()),
    lastName: z.string().min(1, { message: 'Last name is required' }),
    phoneNumber: zCoercedOptional(z.coerce.string().optional()),
    referrerCode: zCoercedOptional(z.string().optional())
      /**
       * Transform the referrer code to a UUID if it is a valid UUID.
       * If it is not a valid UUID, return undefined. We don't want to throw errors for referrer codes.
       */
      .transform((val) => {
        if (!val || val.trim() === '') return undefined;
        try {
          return z.string().uuid().parse(val);
        } catch {
          return undefined;
        }
      }),
    researchKeywordIds: z.array(z.string().uuid()).optional(),
    role: z.nativeEnum(UserProfileRole),
    subcategoryId: zCoercedOptional(z.string().uuid().optional()),
    title: z
      .string()
      .min(TITLE_MIN_LENGTH, { message: TITLE_VALIDATION_MESSAGES.min })
      .max(TITLE_MAX_LENGTH, { message: TITLE_VALIDATION_MESSAGES.max }),
  })
  .strip() satisfies z.ZodType<UserProfileCreateDto>;

/**
 * Schema for validating personal information step in user profile creation.
 * Includes basic personal details like name, title, and contact information.
 * Used in multi-step forms where personal info is collected separately.
 */
export const personalStepSchema = userProfileCreateSchema.pick({
  country: true,
  countryCode: true,
  firstName: true,
  lastName: true,
  phoneNumber: true,
  title: true,
});

/**
 * Schema for validating role selection step in user profile creation.
 * Ensures the user selects a valid role from the available options.
 * Used in multi-step forms where role selection is a separate step.
 */
export const roleStepSchema = userProfileCreateSchema.pick({
  role: true,
});

/**
 * Schema for validating referrer step in user profile creation.
 * Ensures the referrer code is a valid UUID format when provided.
 * Used in multi-step forms where referrer code is collected separately.
 */
export const referrerStepSchema = userProfileCreateSchema.pick({
  referrerCode: true,
});

/**
 * Schema for validating terms and conditions acceptance.
 * Ensures the user accepts the terms and conditions.
 */
export const termsAndConditionsStepSchema = z.object({
  hasAcceptedTerms: z
    .boolean()
    .refine((val) => val, { message: 'You must accept the Terms and Conditions to continue.' }),
});

/**
 * Schema for validating skills step in user profile creation.
 * Includes job role, tech category, and research keywords.
 * All fields are optional.
 */
export const skillsStepSchema = userProfileCreateSchema.pick({
  categoryId: true,
  jobRoleId: true,
  researchKeywordIds: true,
  subcategoryId: true,
});
